import type { CentrifugeMessage } from './messageTypes';

export const isPersonalSocketMessage = (item: CentrifugeMessage): item is PersonalMessage => {
  return 'data' in item && item.data.type === 'personal';
};

type PersonalChannel = `_person:#${string}`;

export interface PersonalUserAlertMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'UserAlert';
      message: string;
    };
  };
}

export interface PersonalUserStopMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'UserStop';
      message: string;
    };
  };
}

export interface PersonalMissionMessageMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'MissionMessage';
      message: string;
      data: {
        title: string;
        message: string;
      };
    };
  };
}

export interface PersonalManagerOnOffMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: string;
      channel: string;
      func: 'ManagerOn' | 'ManagerOff';
    };
  };
}

export interface PersonalRefreshTokenMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'refreshToken';
      channel: string;
      func: 'ManagerOff' | 'ManagerOn';
    };
  };
}

export interface PersonalOverLapLoginMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'OverLapLogin';
      message: string;
      channel: string;
    };
  };
}

export interface PersonalUpdateUserInfoMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'updateUserInfo';
      message: string;
    };
  };
}

export interface PersonalAdminMessage {
  channel?: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'admin' | 'police';
      message: string;
      channel?: string;
      whisper?: 'Y' | 'N';
    };
    created_at?: number;
  };
  offset?: number | undefined;
}

export interface PersonalSupportMessage {
  channel: PersonalChannel;
  data: {
    type: 'personal';
    message: {
      name: 'support';
      idx: string;
      message: string;
      channel: string;
      whisper: 'Y' | 'N';
    };
    created_at: number;
  };
  offset?: number;
}

export const isPersonalUserAlertMessage = (item: PersonalMessage): item is PersonalUserAlertMessage => {
  return 'data' in item && item.data.message.name === 'UserAlert';
};

export const isPersonalUserStopMessage = (item: PersonalMessage): item is PersonalUserStopMessage => {
  return 'data' in item && item.data.message.name === 'UserStop';
};

export const isPersonalMissionMessageMessage = (item: PersonalMessage): item is PersonalMissionMessageMessage => {
  return 'data' in item && item.data.message.name === 'MissionMessage';
};

export const isPersonalRefreshTokenMessage = (item: PersonalMessage): item is PersonalRefreshTokenMessage => {
  return 'data' in item && item.data.message.name === 'refreshToken';
};

export const isPersonalManagerOnOffMessage = (item: PersonalMessage): item is PersonalManagerOnOffMessage => {
  if (!('data' in item)) return false;
  if (!('message' in item.data)) return false;
  if (typeof item.data.message === 'string') return false;
  if (!('func' in item.data.message)) return false;

  return item.data.message.func === 'ManagerOn' || item.data.message.func === 'ManagerOff';
};

export const isPersonalOverLapLoginMessage = (item: PersonalMessage): item is PersonalOverLapLoginMessage => {
  if (!('data' in item)) return false;
  if (!('message' in item.data)) return false;
  if (typeof item.data.message === 'string') return false;
  if (!('name' in item.data.message)) return false;

  return item.data.message.name === 'OverLapLogin';
};

export const isPersonalUpdateUserInfoMessage = (item: PersonalMessage): item is PersonalUpdateUserInfoMessage => {
  if (!('data' in item)) return false;
  if (!('message' in item.data)) return false;
  if (typeof item.data.message === 'string') return false;
  if (!('name' in item.data.message)) return false;

  return item.data.message.name === 'updateUserInfo';
};

export const isPersonalAdminMessage = (item: CentrifugeMessage): item is PersonalAdminMessage => {
  return (
    'data' in item &&
    'message' in item.data &&
    typeof item.data.message === 'object' &&
    item.data.message !== null &&
    'name' in item.data.message &&
    (item.data.message.name === 'admin' || item.data.message.name === 'police')
  );
};

export const isPersonalSupportMessage = (item: CentrifugeMessage): item is PersonalSupportMessage => {
  return (
    'data' in item &&
    'message' in item.data &&
    typeof item.data.message === 'object' &&
    item.data.message !== null &&
    'name' in item.data.message &&
    item.data.message.name === 'support'
  );
};

export type PersonalMessage =
  | PersonalAdminMessage
  | PersonalSupportMessage
  | PersonalOverLapLoginMessage
  | PersonalManagerOnOffMessage
  | PersonalMissionMessageMessage
  | PersonalUserAlertMessage
  | PersonalUserStopMessage
  | PersonalUpdateUserInfoMessage
  | PersonalRefreshTokenMessage;
